import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { toast } from 'react-toastify'

import {
  getStripeRecurring,
  getStripeRecurringCheckoutUrl,
  getStripeSubscriptionsCheckoutUrl,
  SubscriptionPack,
} from '../../api/subscription'
import { AnimatedSpinner } from '../../components/AnimatedSpinner'
import { useBrand } from '../../contexts/BrandContext'
import {
  useCustomerPortalLink,
  useSubscriptionDetails,
} from '../../hooks/apiHooks/subscriptionHooks'
import { OidcUser } from '../../types/oidcUser'
import { Plan } from '../../types/subscription'
import { getOrganizationId } from '../../utils/getOrganization'
import { FAQ } from './components/FAQ'
import { PricingCard } from './components/PricingCard'

import { Link, useSearchParams } from 'react-router-dom'
import { useSubscriptionProductPacks } from '../../hooks/apiHooks/subscriptionHooks'
import { BillingToggle } from './components/BillingToggle'
import { PackCard } from './components/PackCard'
import { SubscriptionCards } from './components/SubscriptionCards'

const getHoursPerMonth = (units: number, interval?: string): number => {
  const delimiter = interval === 'year' ? 12 : 1
  const hoursTotal = units / 60
  return hoursTotal / delimiter
}

function SubscriptionPage() {
  const timerId = React.useRef<number | null>(null)
  const brand = useBrand()
  const { user, isAuthenticated, isLoading } = useAuth()
  const init = React.useRef(true)
  const [pricingPlans, setPricingPlans] = useState<Plan[]>([])

  const [isAnnual, setIsAnnual] = useState(false)
  const [loading, setLoading] = useState(false)
  const [freeSubsName, setFreeSubsName] = useState<string>('')

  const [isTokenExpired, setIsTokenExpired] = useState(false)

  const orgId = getOrganizationId(user as OidcUser)

  const [searchParams, setSearchParams] = useSearchParams()

  const [purchasedSubscriptionName] = useState<string>(
    searchParams.get('purchasedSubscription') || '',
  )

  const {
    data: subscriptionData,
    isError: isSubscriptionError,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useSubscriptionDetails(
    { organization_id: orgId },
    isAuthenticated,
    purchasedSubscriptionName,
  )

  useEffect(() => {
    if (searchParams.get('purchasedPackName')) {
      toast.success(
        `Congratulations! You’ve successfully purchased the ${searchParams.get('purchasedPackName')}.`,
      )
      searchParams.delete('purchasedPackName')
      setSearchParams(searchParams)
    }

    if (searchParams.get('purchasedSubscription')) {
      searchParams.delete('purchasedSubscription')
      setSearchParams(searchParams)
    }
  }, [])

  const {
    data: customLinkData,
    isError: isCustomerLinkError,
    error: customerLinkError,
    isLoading: isCustomerLinkLoading,
    refetch,
  } = useCustomerPortalLink({ token: user?.access_token || '' })

  const { data: subscriptionPacks } = useSubscriptionProductPacks()

  const handleAuthError = () => {
    setIsTokenExpired(true)
    if (timerId.current) {
      clearInterval(timerId.current)
      timerId.current = null
    }
  }

  useEffect(() => {
    if (
      subscriptionData &&
      user?.access_token &&
      !isTokenExpired &&
      subscriptionData?.billingDetails.paymentType !== 'Free'
    ) {
      void refetch()
      timerId.current = window.setInterval(() => {
        void refetch()
      }, 2 * 60000)
    }
    return () => {
      if (timerId.current) clearInterval(timerId.current)
    }
  }, [subscriptionData, user?.access_token, isTokenExpired])

  useEffect(() => {
    if (isSubscriptionError) {
      if (subscriptionError.status === 401) {
        handleAuthError()
      }
    }
  }, [isSubscriptionError, subscriptionError])

  useEffect(() => {
    if (isCustomerLinkError) {
      if (customerLinkError.status === 401) {
        handleAuthError()
      }
    }
  }, [isCustomerLinkError, customerLinkError])

  useEffect(
    () => () => {
      if (timerId.current) clearInterval(timerId.current)
    },
    [],
  )
  const groupedPlans = useMemo(() => {
    const monthly = pricingPlans.filter(plan => plan.per === 'month')
    const annual = pricingPlans.filter(plan => plan.per === 'year')
    return { monthly, annual }
  }, [pricingPlans])

  useEffect(() => {
    if (init.current) {
      setLoading(true)
      getStripeRecurring()
        .then(response => {
          const out = response.data
            .sort((a: Plan, b: Plan) => a.price - b.price)
            .map((item: Plan) => {
              if (item.price === 0) {
                setFreeSubsName(item.name)
              }
              return {
                ...item,
                per: item.interval,
                hours: getHoursPerMonth(item.units, item.interval) + '/month',
                showBestValue: true,
              }
            })
          setPricingPlans(out)
          init.current = false
        })
        .catch((error: unknown) => {
          if (error && typeof error === 'object' && 'error' in error) {
            toast.error(error.error as string)
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [user?.access_token])

  const packs = useMemo(() => {
    if (!subscriptionPacks || subscriptionPacks.data.length === 0) {
      return [[], []]
    }
    const hardwarePack: SubscriptionPack[] = []
    const packs = subscriptionPacks.data
      .sort((a: SubscriptionPack, b: SubscriptionPack) => a.price - b.price)
      .filter(pack => {
        if (pack.isHardware === true) {
          hardwarePack.push(pack)
          return false
        }
        return true
      })
      .map(pack => {
        return {
          ...pack,
          hours: String(pack.units / 60),
        }
      })

    return [packs, hardwarePack]
  }, [subscriptionPacks])

  const handleCheckout = (
    price_id: string,
    cancelUrl: string,
    name: string,
  ) => {
    const returnUrl = cancelUrl + `?purchasedSubscription=${name}`
    getStripeRecurringCheckoutUrl(price_id, returnUrl, cancelUrl)
      .then(response => {
        window.location.href = response.data.url
      })
      .catch((error: Record<string, string>) => {
        toast.error(error.error)
      })
  }

  const buyPacks = (price_id: string, cancelUrl: string, name: string) => {
    const returnUrl = cancelUrl + `?purchasedPackName=${name}`
    getStripeSubscriptionsCheckoutUrl(price_id, returnUrl, cancelUrl)
      .then(response => {
        window.location.href = response.data.url
      })
      .catch((error: Record<string, string>) => {
        toast.error(error.error)
      })
  }

  if (isLoading) {
    return null
  }

  return (
    <div className="tc-mv-card-wrapper font-dmsans">
      <div className="max-w-[1244px] mx-auto my-24 sm:px-6 lg:px-8 py-8">
        <div className="gradient-background" />
        <div className=" bg-transparent transition-colors">
          {/* Hero Section */}
          <div className="mx-auto pt-10 px-5 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center">
              <img src={brand.logo} className="w-[125px]" alt="logo" />
            </div>
            <div className="flex justify-center items-center text-center text-white text-[48px] md:text-[64px] font-normal mb-6 font-seriftext">
              Plans &amp; Pricing
            </div>
            <div className="text-center mt-7 mb-16 ">
              <p className="text-xl text-white max-w-7xl mx-auto">
                Video chat and collaborate live with a universe of AI agents
                trained on an almost limitless number of topics from accounting,
                to coding, personalized fitness and meal plans, and so much
                more. Don't see a{' '}
                <Link to="/" className="underline text-white">
                  Companion
                </Link>{' '}
                that fits your needs? Just ask, and an agent will be generated
                for you.
              </p>
              <p className="text-xl text-white max-w-7xl mx-auto mt-6 font-extrabold">
                Your first 100 minutes of chat are on us!
              </p>
            </div>
            {/* Pricing Section */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <AnimatedSpinner className="w-8 h-8 text-white" />
              </div>
            ) : (
              <div className="mt-11">
                {/* anchor for scroll to view */}
                <div id="view" />
                {packs[1] && !!packs[1].length && (
                  <div className="mt-11">
                    <div className="text-white text-40px font-normal mb-1 font-seriftext">
                      The Napster View
                    </div>
                    <div className="text-white text-[18px] mb-6">
                      Clip a{' '}
                      <Link
                        className="underline"
                        to="https://napster.ai/view"
                        target="_blank"
                        data-testid="napster-view-link"
                      >
                        Napster View
                      </Link>{' '}
                      to your monitor to add a second screen for streaming AI
                      conversations in 3D!
                    </div>
                    <PricingCard
                      key={packs[1][0].name}
                      {...packs[1][0]}
                      onCheckout={buyPacks}
                      isAuthenticated={isAuthenticated}
                      packCard
                      isHardware
                    />
                  </div>
                )}
                <div className="shrink-0 text-white text-4xl font-normal mb-6 font-['DM_Serif_Text'] flex md:items-center items-start justify-between gap-5 md:flex-row flex-col mt-11">
                  <span>Subscriptions</span>{' '}
                  <div className="shrink-0">
                    <BillingToggle isAnnual={isAnnual} onChange={setIsAnnual} />
                  </div>
                </div>
                <div className="flex justify-center gap-4 flex-wrap [@media(min-width:1300px)]:flex-nowrap">
                  <SubscriptionCards
                    plans={
                      isAnnual ? groupedPlans.annual : groupedPlans.monthly
                    }
                    onCheckout={handleCheckout}
                    subPriceId={subscriptionData?.billingDetails.priceId}
                    subsPlanName={subscriptionData?.billingDetails.planName}
                    isAuthenticated={isAuthenticated}
                    link={
                      isAuthenticated ? customLinkData?.data.url : undefined
                    }
                    freeSubsName={freeSubsName}
                    isLoading={
                      isSubscriptionLoading ||
                      isCustomerLinkLoading ||
                      (!!purchasedSubscriptionName &&
                        subscriptionData?.billingDetails.planName !==
                          purchasedSubscriptionName)
                    }
                  />
                </div>
                <div className="text-white text-sm font-normal leading-[150%] mt-4">
                  *One free View device per user/account with the first Annual
                  year subscription. Not valid on renewals. Taxes are extra.
                  U.S. only.
                </div>
                <div className="mt-11">
                  <div className="text-white text-40px font-normal mb-6 font-seriftext">
                    Packs
                  </div>
                  <div className="flex justify-center gap-4 flex-col">
                    {packs[0].map(pack => (
                      <PackCard
                        key={pack.name}
                        {...pack}
                        onCheckout={buyPacks}
                        isAuthenticated={isAuthenticated}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* <div className="max-w-7xl m-auto">
            <EnterpriseCard />
          </div> */}
            <FAQ />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPage
